/* =====================================================================================
   How current the model is, source by source.

   app.data_currency already tracked 24 sources and gave every one of them the same four-month
   recheck interval, which is the module's default rather than a decision about any of them. That
   is wrong in both directions at once: NHSBT publishes once a year, so two of those three checks
   are wasted; SRTR releases quarterly, so a four-month cycle misses a release roughly every third
   year and nobody notices, because a stale figure looks exactly like a current one.

   The register now carries the SOURCE's own rhythm, so "overdue" means a new release is expected
   and we have not loaded it, rather than an arbitrary clock running out.

   ALL OF IT IS PER-ORG DATA. A tenant in another market has different sources with different
   rhythms; cadence is a shape, not a subject.
   ===================================================================================== */

import { BACKEND } from './db';

export interface SourceFreshness {
  id: string; topic: string; source_name: string | null; source_url: string | null;
  cadence: string | null; release_month: number | null; automation: string;
  priority: string; last_touch: string | null; due_on: string | null;
  days_overdue: number; state: string;
}

export async function fetchFreshness(): Promise<SourceFreshness[]> {
  if (BACKEND !== 'supabase') return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').from('v_source_freshness')
    .select('*').order('days_overdue', { ascending: false }).order('due_on');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as SourceFreshness[];
}

/** Records that a source has been reloaded today, which is what clears an overdue row. */
export async function markLoaded(id: string): Promise<void> {
  if (BACKEND !== 'supabase') return;
  const { supabase } = await import('./supabase');
  const today = new Date().toISOString().slice(0, 10);
  const { error } = await supabase().schema('app').from('data_currency')
    .update({ last_loaded_on: today, last_verified_on: today }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** Changing the rhythm. Editable because the publisher changes theirs and we should follow. */
export async function setCadence(id: string, patch: {
  cadence?: string; release_month?: number | null; recheck_months?: number; priority?: string;
}): Promise<void> {
  if (BACKEND !== 'supabase') return;
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('data_currency').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}
